<template>
    <div class="py-12">
        <inertia-link :href="route('kiosk.app-env-vars.index')">
            <AtomicButton color="yellow" size="sm">
                {{ $t("vue.back_to_app_env_vars") }}
                <template #icon>
                    <BackspaceIcon
                        class="mr-2 h-6 w-6 transition duration-200 group-hover:text-gray-600"
                    />
                </template>
            </AtomicButton>
        </inertia-link>

        <h1
            class="mt-2 text-2xl font-medium leading-normal tracking-small text-gray-800"
        >
            {{ $t("vue.edit") }}
        </h1>

        <form class="flex flex-col" @submit.prevent="updateAppEnvVar">
            <TextInput
                v-model="v$.form.key.$model"
                class="my-6 w-52 sm:w-80"
                :label="$t('vue.key')"
                type="text"
                :placeholder="$t('vue.max_255_characters')"
                :error="getError('key')"
            />

            <SelectInput
                v-model="state.form.value_type"
                class="mb-6 w-52 sm:w-80"
                :data="typeOptions"
                :label="$t('vue.value_type')"
                :placeholder="$t('vue.value_type')"
                :error="getError('value_type')"
            />

            <TextInput
                v-model="v$.form.value.$model"
                class="mb-6 w-52 sm:w-80"
                :label="$t('vue.value')"
                type="text"
                :error="getError('value')"
            />

            <div class="mt-4 flex flex-row">
                <AtomicButton
                    type="submit"
                    color="yellow"
                    size="sm"
                    :disabled="loading || v$.$invalid"
                >
                    {{ $t("vue.save_changes") }}
                </AtomicButton>

                <AtomicButton
                    class="ml-2"
                    size="sm"
                    color="red"
                    @click.prevent="deleteAppEnvVar"
                >
                    {{ $t("vue.delete") }}
                    <template #icon>
                        <TrashIcon
                            class="mr-2 h-6 w-6 transition duration-200 group-hover:text-gray-600"
                        />
                    </template>
                </AtomicButton>
            </div>
        </form>
    </div>
</template>

<script lang="ts" setup>
import useVuelidate from "@vuelidate/core";
import { BackspaceIcon, TrashIcon } from "@heroicons/vue/24/outline";
import { required, maxLength } from "@vuelidate/validators";
import route from "ziggy-js";
import { router } from "@inertiajs/vue3";
import type IInputErrors from "@/types/Atomic/IInputErrors";
import type IAppEnvVar from "@/types/AppEnvVars/IAppEnvVar";
import type { AppEnvVarValueType } from "@/types/AppEnvVars/IAppEnvVar";
import { create } from "@/composables/useNotifications";
import useConfirm from "@/composables/useConfirm";

const props = defineProps<{
    appEnvVar: IAppEnvVar;
    valueTypes: AppEnvVarValueType[];
}>();

const { show } = useConfirm();

const typeOptions = props.valueTypes.map((type) => ({
    title: type,
    value: type,
}));

const state = reactive({
    form: {
        key: props.appEnvVar.key,
        value: props.appEnvVar.value,
        value_type: props.appEnvVar.value_type,
    },
});

const rules = {
    form: {
        key: {
            required,
            maxLength: maxLength(255),
        },
        value: {
            required,
            maxLength: maxLength(65535),
        },
        value_type: {
            required,
        },
    },
};

const v$ = useVuelidate(rules, state);
const loading = ref(false);
const errors = ref<IInputErrors>({});

const updateAppEnvVar = () => {
    v$.value.$touch();
    if (v$.value.form.$error) {
        return;
    }

    loading.value = true;
    router.put(
        route("kiosk.app-env-vars.update", {
            appEnvVar: props.appEnvVar.id,
        }),
        state.form,
        {
            preserveScroll: true,
            onError: (err) => {
                errors.value = err;
            },
            onSuccess: () => {
                errors.value = {};
            },
            onFinish: () => {
                loading.value = false;
            },
        },
    );
};

const deleteAppEnvVar = async () => {
    const confirmed = await show({
        header: $t("vue.delete"),
        message: $t("vue.delete_app_env_var_message"),
        type: "danger",
        confirm: $t("vue.delete"),
    });

    if (confirmed) {
        router.delete(
            route("kiosk.app-env-vars.destroy", {
                appEnvVar: props.appEnvVar.id,
            }),
            {
                preserveScroll: true,
                onSuccess: () => {
                    create({
                        title: $t("vue.app_env_var_deleted"),
                        type: "success",
                    });
                },
                onError: () => {
                    create({
                        title: $t("vue.app_env_var_not_deleted"),
                        type: "error",
                    });
                },
            },
        );
    }
};

const getError = (field: string) => {
    return (
        v$.value.form[field]?.$errors[0]?.$message ||
        errors.value?.[field] ||
        ""
    );
};
</script>
